export type AuthTransitionEventName =
  | 'auth_recovery_started'
  | 'auth_recovery_timeout'
  | 'auth_recovery_completed'
  | 'onboarding_redirected'
  | 'auth_bootstrap_slo_snapshot'
  | 'auth_bootstrap_alert';

type AuthTransitionEventInput = {
  from: string;
  to: string;
  reason: string;
  durationMs?: number;
  userId?: string | null;
};

type AuthBootstrapOutcome = 'success' | 'timeout' | 'loop';

type AuthBootstrapSample = {
  duration_ms: number;
  outcome: AuthBootstrapOutcome;
  ts_ms: number;
};

type AuthBootstrapSloSnapshot = {
  p50_ms: number;
  p95_ms: number;
  timeout_rate: number;
  loop_rate: number;
  sample_count: number;
  ts_ms: number;
};

const AUTH_BOOTSTRAP_SAMPLES_KEY = 'flowqa-auth-bootstrap-samples';
const AUTH_BOOTSTRAP_SAMPLE_LIMIT = 60;
const AUTH_BOOTSTRAP_ALERT_P95_MS = 8_000;
const AUTH_BOOTSTRAP_ALERT_TIMEOUT_RATE = 0.2;
const AUTH_BOOTSTRAP_ALERT_LOOP_RATE = 0.1;

const hashUserId = (userId?: string | null): string | null => {
  if (!userId) return null;
  let hash = 2166136261;
  for (let index = 0; index < userId.length; index += 1) {
    hash ^= userId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a_${(hash >>> 0).toString(16)}`;
};

export const emitAuthTransitionEvent = (
  event: AuthTransitionEventName,
  input: AuthTransitionEventInput,
): void => {
  if (typeof window === 'undefined') return;
  const detail = {
    event,
    from: input.from,
    to: input.to,
    reason: input.reason,
    duration_ms: input.durationMs ?? 0,
    userId_hash: hashUserId(input.userId),
    ts_ms: Date.now(),
  };
  window.dispatchEvent(new CustomEvent(event, { detail }));
  window.dispatchEvent(new CustomEvent('flowqa-auth-transition', { detail }));
};

const safeReadSamples = (): AuthBootstrapSample[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(AUTH_BOOTSTRAP_SAMPLES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is AuthBootstrapSample =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { duration_ms?: unknown }).duration_ms === 'number' &&
        typeof (entry as { outcome?: unknown }).outcome === 'string' &&
        typeof (entry as { ts_ms?: unknown }).ts_ms === 'number',
    );
  } catch {
    return [];
  }
};

const safeWriteSamples = (samples: AuthBootstrapSample[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTH_BOOTSTRAP_SAMPLES_KEY, JSON.stringify(samples));
  } catch {
    // ignore storage failures
  }
};

const percentile = (values: number[], pct: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * pct)));
  return sorted[index] ?? 0;
};

const buildSnapshot = (samples: AuthBootstrapSample[]): AuthBootstrapSloSnapshot => {
  const durations = samples.map((sample) => sample.duration_ms);
  const timeoutCount = samples.filter((sample) => sample.outcome === 'timeout').length;
  const loopCount = samples.filter((sample) => sample.outcome === 'loop').length;
  const sampleCount = samples.length;
  return {
    p50_ms: percentile(durations, 0.5),
    p95_ms: percentile(durations, 0.95),
    timeout_rate: sampleCount > 0 ? timeoutCount / sampleCount : 0,
    loop_rate: sampleCount > 0 ? loopCount / sampleCount : 0,
    sample_count: sampleCount,
    ts_ms: Date.now(),
  };
};

const emitBootstrapSnapshot = (snapshot: AuthBootstrapSloSnapshot): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('auth_bootstrap_slo_snapshot', {
      detail: { event: 'auth_bootstrap_slo_snapshot', ...snapshot },
    }),
  );
  window.dispatchEvent(
    new CustomEvent('flowqa-auth-transition', {
      detail: { event: 'auth_bootstrap_slo_snapshot', ...snapshot },
    }),
  );
};

const emitBootstrapAlert = (snapshot: AuthBootstrapSloSnapshot, reason: string): void => {
  if (typeof window === 'undefined') return;
  const detail = { event: 'auth_bootstrap_alert', reason, ...snapshot };
  window.dispatchEvent(new CustomEvent('auth_bootstrap_alert', { detail }));
  window.dispatchEvent(new CustomEvent('flowqa-auth-transition', { detail }));
};

export const recordAuthBootstrapSample = (input: {
  durationMs: number;
  outcome: AuthBootstrapOutcome;
}): void => {
  if (typeof window === 'undefined') return;
  const samples = safeReadSamples();
  const nextSamples = [
    ...samples,
    {
      duration_ms: Math.max(0, Math.round(input.durationMs)),
      outcome: input.outcome,
      ts_ms: Date.now(),
    },
  ].slice(-AUTH_BOOTSTRAP_SAMPLE_LIMIT);
  safeWriteSamples(nextSamples);
  const snapshot = buildSnapshot(nextSamples);
  emitBootstrapSnapshot(snapshot);
  if (snapshot.p95_ms > AUTH_BOOTSTRAP_ALERT_P95_MS) {
    emitBootstrapAlert(snapshot, 'p95_exceeded');
  } else if (snapshot.timeout_rate > AUTH_BOOTSTRAP_ALERT_TIMEOUT_RATE) {
    emitBootstrapAlert(snapshot, 'timeout_rate_spike');
  } else if (snapshot.loop_rate > AUTH_BOOTSTRAP_ALERT_LOOP_RATE) {
    emitBootstrapAlert(snapshot, 'loop_rate_spike');
  }
};
